type EventCallback<T = unknown> = (data: T) => void;
type EventMap = Record<string, unknown>;

class EventBus {
  private listeners: Map<string, Set<EventCallback>> = new Map();

  /**
   * Subscribe to an event
   * @param eventName The event name to subscribe to
   * @param callback Function to be called when event is triggered
   * @returns A function to unsubscribe
   */
  on<K extends keyof EventMap>(eventName: K, callback: EventCallback<EventMap[K]>): () => void {
    if (!this.listeners.has(eventName as string)) {
      this.listeners.set(eventName as string, new Set());
    }

    const callbacks = this.listeners.get(eventName as string)!;
    callbacks.add(callback as EventCallback);

    return () => {
      this.off(eventName, callback);
    };
  }

  /**
   * Unsubscribe from an event
   * @param eventName The event name to unsubscribe from
   * @param callback The callback function to remove
   */
  off<K extends keyof EventMap>(eventName: K, callback: EventCallback<EventMap[K]>): void {
    if (!this.listeners.has(eventName as string)) {
      return;
    }

    const callbacks = this.listeners.get(eventName as string)!;
    callbacks.delete(callback as EventCallback);

    if (callbacks.size === 0) {
      this.listeners.delete(eventName as string);
    }
  }

  /**
   * Emit an event with data
   * @param eventName The event to emit
   * @param data Data to pass to subscribers
   */
  emit<K extends keyof EventMap>(eventName: K, data: EventMap[K]): void {
    if (!this.listeners.has(eventName as string)) {
      return;
    }

    const callbacks = this.listeners.get(eventName as string)!;
    callbacks.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in event handler for "${eventName}":`, error);
      }
    });
  }

  clear(): void {
    this.listeners.clear();
  }

  /**
   * Get the number of listeners for a specific event
   * @param eventName The event name
   * @returns Number of listeners
   */
  listenerCount<K extends keyof EventMap>(eventName: K): number {
    if (!this.listeners.has(eventName as string)) {
      return 0;
    }
    return this.listeners.get(eventName as string)!.size;
  }
}

export interface EventTypes {
  'travelplannerfeedbackform:show': undefined;
}

export const eventBus = new EventBus() as {
  on<K extends keyof EventTypes>(event: K, callback: EventCallback<EventTypes[K]>): () => void;
  off<K extends keyof EventTypes>(event: K, callback: EventCallback<EventTypes[K]>): void;
  emit<K extends keyof EventTypes>(event: K, data: EventTypes[K]): void;
  clear(): void;
  listenerCount<K extends keyof EventTypes>(event: K): number;
};

export default eventBus;
